/**
 * Durable, client-side persistence for in-flight packing recordings.
 *
 * The packing "proof of pack" video is captured by an in-memory MediaRecorder
 * living in the app-root recorder context. That clip survives in-app
 * navigation, but a full browser refresh/close (or a tab crash) used to throw
 * the in-memory chunks away — only a `beforeunload` warning stood between the
 * operator and a lost clip.
 *
 * This module mirrors every chunk the recorder produces into IndexedDB, keyed
 * to the packing job, so that after an interruption the captured bytes are
 * still on disk and can be reassembled into a File and attached to the job.
 * The persisted data is cleared once the clip has been successfully uploaded.
 *
 * Everything here degrades gracefully: if IndexedDB is unavailable (private
 * mode, ancient browser, blocked storage) the helpers resolve to no-ops /
 * empty results so the recorder still works — it just loses the crash-recovery
 * guarantee, exactly as before this module existed.
 *
 * DB_VERSION history:
 *   1 — sessions + chunks stores (video recording crash-recovery)
 *   2 — pendingStills store (still-capture WAL: object uploaded but not yet
 *       registered; drained on next session load to prevent GCS orphans)
 */

const DB_NAME = "eb-packing-recordings";
const DB_VERSION = 2;
const SESSION_STORE = "sessions";
const CHUNK_STORE = "chunks";
const PENDING_STILLS_STORE = "pendingStills";

export interface RecordingSessionMeta {
  /** The packing job this recording belongs to (primary key). */
  jobId: string;
  /** MediaRecorder mime type, used to rebuild the Blob/File with the right type. */
  mimeType: string;
  /** File-name prefix to use when reassembling the recovered clip. */
  prefix: string;
  /** Epoch ms when the recording started. */
  startedAt: number;
  /** Epoch ms of the last chunk written — lets the UI show "how old". */
  updatedAt: number;
  // --- Progressive-upload state (Task #1252, all optional & additive) ---
  // These are written by the recording worker as it uploads the clip in
  // aligned byte-ranges WHILE recording. They are persisted so progress is
  // observable and (future) so an interrupted session could in principle be
  // resumed; today's crash-recovery still goes through the full-upload path
  // (`loadRecoveredRecording`), so these fields are purely advisory. Stored as
  // extra keys on the existing schemaless `sessions` object store — no
  // DB_VERSION bump (a version change would fire `onblocked` against other open
  // tabs and degrade IndexedDB for the active tab, the one regression we must
  // avoid here).
  /** GCS resumable session URL the worker PUTs ranges to. */
  uploadSessionUrl?: string;
  /** Canonical `/objects/uploads/<uuid>` path the attach + serve routes use. */
  uploadObjectPath?: string;
  /** Content type the session was minted with. */
  uploadContentType?: string;
  /** File name to attach the finished object under. */
  uploadFileName?: string;
  /** Bytes confirmed committed to GCS so far. */
  uploadCommittedBytes?: number;
  /** True once the final range PUT has succeeded. */
  uploadComplete?: boolean;
}

interface ChunkRecord {
  /** Auto-increment key. */
  id?: number;
  jobId: string;
  /** Monotonic sequence so chunks reassemble in capture order. */
  seq: number;
  blob: Blob;
}

/**
 * A still-capture that has been PUT to GCS but not yet registered via the
 * `/api/packing/jobs/:jobId/media` POST. Written AFTER a successful GCS PUT,
 * cleared AFTER a successful POST. Any records left behind by a crashed/closed
 * tab are drained on the next session load so the GCS objects are registered
 * and appear in the job gallery rather than being silently orphaned.
 */
export interface PendingStillRecord {
  /** Auto-increment primary key. Used to delete the record after registration. */
  id?: number;
  /** The packing job this still belongs to. */
  jobId: string;
  /** GCS object path returned by the mint endpoint. */
  objectPath: string;
  /** File name used when the still was captured. */
  fileName: string;
  /** Always "image/jpeg". */
  contentType: string;
  /** Size of the uploaded JPEG in bytes. */
  sizeBytes: number;
  /** Epoch ms when the record was written (for stale-record pruning in future). */
  savedAt: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === "undefined") {
      resolve(null);
      return;
    }
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(SESSION_STORE)) {
        db.createObjectStore(SESSION_STORE, { keyPath: "jobId" });
      }
      if (!db.objectStoreNames.contains(CHUNK_STORE)) {
        const store = db.createObjectStore(CHUNK_STORE, {
          keyPath: "id",
          autoIncrement: true,
        });
        store.createIndex("jobId", "jobId", { unique: false });
      }
      // v2: pending-stills WAL store (still uploaded but not yet registered).
      if (!db.objectStoreNames.contains(PENDING_STILLS_STORE)) {
        const store = db.createObjectStore(PENDING_STILLS_STORE, {
          keyPath: "id",
          autoIncrement: true,
        });
        store.createIndex("jobId", "jobId", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  return dbPromise;
}

function tx(
  db: IDBDatabase,
  stores: string[],
  mode: IDBTransactionMode,
): IDBTransaction {
  return db.transaction(stores, mode);
}

function reqDone<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// In-memory sequence counters per job so concurrent chunk writes keep order
// even before the auto-increment id is assigned.
const seqByJob = new Map<string, number>();

/**
 * Start (or restart) a durable session for a job. Clears any previously
 * persisted chunks/meta for that job first, so a fresh "Start packing" never
 * stacks new bytes onto a stale recording.
 */
export async function beginRecordingSession(
  meta: Omit<RecordingSessionMeta, "updatedAt">,
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await clearRecordingSession(meta.jobId);
  seqByJob.set(meta.jobId, 0);
  try {
    const t = tx(db, [SESSION_STORE], "readwrite");
    t.objectStore(SESSION_STORE).put({
      ...meta,
      updatedAt: Date.now(),
    } satisfies RecordingSessionMeta);
    await new Promise<void>((resolve) => {
      t.oncomplete = () => resolve();
      t.onerror = () => resolve();
      t.onabort = () => resolve();
    });
  } catch {
    /* best-effort */
  }
}

/** Append one recorder chunk to the durable store for a job. */
export async function appendRecordingChunk(
  jobId: string,
  blob: Blob,
): Promise<void> {
  if (!blob || blob.size === 0) return;
  const db = await openDb();
  if (!db) return;
  const seq = (seqByJob.get(jobId) ?? 0) + 1;
  seqByJob.set(jobId, seq);
  try {
    const t = tx(db, [SESSION_STORE, CHUNK_STORE], "readwrite");
    t.objectStore(CHUNK_STORE).add({ jobId, seq, blob } satisfies ChunkRecord);
    // Touch the session's updatedAt so recovery can show recency.
    const sessionStore = t.objectStore(SESSION_STORE);
    const getReq = sessionStore.get(jobId);
    getReq.onsuccess = () => {
      const existing = getReq.result as RecordingSessionMeta | undefined;
      if (existing) {
        sessionStore.put({ ...existing, updatedAt: Date.now() });
      }
    };
    await new Promise<void>((resolve) => {
      t.oncomplete = () => resolve();
      t.onerror = () => resolve();
      t.onabort = () => resolve();
    });
  } catch {
    /* best-effort */
  }
}

/**
 * Patch additive fields (progressive-upload state) onto an existing session
 * meta record. No-op when the session doesn't exist yet or IndexedDB is
 * unavailable. Best-effort — never throws into the caller (the worker).
 */
export async function patchRecordingSession(
  jobId: string,
  patch: Partial<RecordingSessionMeta>,
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const t = tx(db, [SESSION_STORE], "readwrite");
    const store = t.objectStore(SESSION_STORE);
    const getReq = store.get(jobId);
    getReq.onsuccess = () => {
      const existing = getReq.result as RecordingSessionMeta | undefined;
      if (existing) {
        store.put({ ...existing, ...patch, jobId, updatedAt: Date.now() });
      }
    };
    await new Promise<void>((resolve) => {
      t.oncomplete = () => resolve();
      t.onerror = () => resolve();
      t.onabort = () => resolve();
    });
  } catch {
    /* best-effort */
  }
}

/**
 * List the meta for every persisted recording session, most-recently-active
 * first. Used by the background auto-flush to find clips left behind by
 * interrupted sessions across ALL jobs — not just the one currently on screen.
 * Degrades to an empty array when IndexedDB is unavailable.
 */
export async function listRecordingSessions(): Promise<RecordingSessionMeta[]> {
  const db = await openDb();
  if (!db) return [];
  try {
    const t = tx(db, [SESSION_STORE], "readonly");
    const all = await reqDone(
      t.objectStore(SESSION_STORE).getAll() as IDBRequest<
        RecordingSessionMeta[]
      >,
    );
    if (!all) return [];
    return all.sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
  } catch {
    return [];
  }
}

/** Return the persisted session meta for a job, or null if none. */
export async function getRecordingSession(
  jobId: string,
): Promise<RecordingSessionMeta | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const t = tx(db, [SESSION_STORE], "readonly");
    const meta = await reqDone(
      t.objectStore(SESSION_STORE).get(jobId) as IDBRequest<
        RecordingSessionMeta | undefined
      >,
    );
    return meta ?? null;
  } catch {
    return null;
  }
}

/**
 * Reassemble the persisted chunks for a job into a single File ready to
 * upload. Returns null when there is no recoverable data (no session, no
 * chunks, or zero total bytes).
 */
export async function loadRecoveredRecording(
  jobId: string,
): Promise<File | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const meta = await getRecordingSession(jobId);
    if (!meta) return null;
    const t = tx(db, [CHUNK_STORE], "readonly");
    const index = t.objectStore(CHUNK_STORE).index("jobId");
    const records = await reqDone(
      index.getAll(IDBKeyRange.only(jobId)) as IDBRequest<ChunkRecord[]>,
    );
    if (!records || records.length === 0) return null;
    records.sort((a, b) => a.seq - b.seq);
    const blob = new Blob(
      records.map((r) => r.blob),
      { type: meta.mimeType || "video/webm" },
    );
    if (blob.size === 0) return null;
    const ext = (meta.mimeType || "").includes("mp4") ? "mp4" : "webm";
    const ts = new Date(meta.startedAt || Date.now())
      .toISOString()
      .replace(/[:.]/g, "-");
    return new File([blob], `${meta.prefix || "packing-proof"}-recovered-${ts}.${ext}`, {
      type: meta.mimeType || "video/webm",
    });
  } catch {
    return null;
  }
}

/** Delete the persisted session + all chunks for a job. */
export async function clearRecordingSession(jobId: string): Promise<void> {
  seqByJob.delete(jobId);
  const db = await openDb();
  if (!db) return;
  try {
    const t = tx(db, [SESSION_STORE, CHUNK_STORE], "readwrite");
    t.objectStore(SESSION_STORE).delete(jobId);
    const index = t.objectStore(CHUNK_STORE).index("jobId");
    const cursorReq = index.openKeyCursor(IDBKeyRange.only(jobId));
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor) {
        t.objectStore(CHUNK_STORE).delete(cursor.primaryKey);
        cursor.continue();
      }
    };
    await new Promise<void>((resolve) => {
      t.oncomplete = () => resolve();
      t.onerror = () => resolve();
      t.onabort = () => resolve();
    });
  } catch {
    /* best-effort */
  }
}

// ---------------------------------------------------------------------------
// Pending-stills WAL (write-ahead log)
// ---------------------------------------------------------------------------
// Each record represents a still image that was successfully PUT to GCS but
// whose /api/packing/jobs/:jobId/media POST has not yet been confirmed. Records
// are written BEFORE the network call that can fail/be interrupted, and deleted
// only after a confirmed success, so a tab closure between PUT and POST leaves
// recoverable state that the next session drains.

/**
 * Persist a pending-still record AFTER the GCS PUT succeeds and BEFORE the
 * registration POST. Returns the auto-increment id on success (used to delete
 * the record later), or null when IndexedDB is unavailable.
 */
export async function addPendingStill(
  record: Omit<PendingStillRecord, "id">,
): Promise<number | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const t = tx(db, [PENDING_STILLS_STORE], "readwrite");
    const req = t.objectStore(PENDING_STILLS_STORE).add(record);
    await new Promise<void>((resolve) => {
      t.oncomplete = () => resolve();
      t.onerror = () => resolve();
      t.onabort = () => resolve();
    });
    const id = req.result;
    return typeof id === "number" ? id : null;
  } catch {
    return null;
  }
}

/**
 * Remove a pending-still record by its auto-increment id once the registration
 * POST has succeeded. Best-effort — never throws.
 */
export async function removePendingStill(id: number): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const t = tx(db, [PENDING_STILLS_STORE], "readwrite");
    t.objectStore(PENDING_STILLS_STORE).delete(id);
    await new Promise<void>((resolve) => {
      t.oncomplete = () => resolve();
      t.onerror = () => resolve();
      t.onabort = () => resolve();
    });
  } catch {
    /* best-effort */
  }
}

/**
 * Return all pending-still records, ordered oldest-first so the drain loop
 * processes them in the order they were captured. Degrades to an empty array
 * when IndexedDB is unavailable.
 */
export async function listPendingStills(): Promise<PendingStillRecord[]> {
  const db = await openDb();
  if (!db) return [];
  try {
    const t = tx(db, [PENDING_STILLS_STORE], "readonly");
    const all = await reqDone(
      t.objectStore(PENDING_STILLS_STORE).getAll() as IDBRequest<
        PendingStillRecord[]
      >,
    );
    if (!all) return [];
    return all.sort((a, b) => (a.savedAt ?? 0) - (b.savedAt ?? 0));
  } catch {
    return [];
  }
}
